import { api } from "@/lib/api/api";
import { FpApiError } from "@/lib/api/errors";
import type { ParsedParam } from "@/lib/mcp/schema";
import { useInspectorStore } from "@/stores/inspectorStore";
import { useCallback } from "react";
import {
  createExecutionHistoryEntry,
  createPromptExecutionRequest,
  extractMcpResult,
} from "../shared/utils/execution";
import { coerceAllParameters } from "../shared/utils/validation";

export interface UsePromptExecutionOptions {
  projectId: string;
  promptName: string | null;
  parsedParams: ParsedParam[];
}

export function usePromptExecution({
  projectId,
  promptName,
  parsedParams,
}: UsePromptExecutionOptions) {
  const {
    getPromptState,
    setPromptExecuting,
    setPromptResponse,
    setPromptError,
    addPromptExecution,
  } = useInspectorStore();

  const currentState = promptName ? getPromptState(promptName) : null;
  const isLoading = currentState?.isExecuting || false;
  const result = currentState?.lastResponse || null;
  const error = currentState?.lastError || null;
  const arguments_ = currentState?.arguments || {};
  const argumentErrors = currentState?.argumentErrors || {};

  const executePrompt = useCallback(async () => {
    if (!promptName || !projectId) {
      return;
    }

    const startTime = Date.now();

    try {
      // Coerce arguments to correct types
      const { args, errors } = coerceAllParameters(arguments_, parsedParams);
      const combinedErrors: Record<string, string | undefined> = {
        ...argumentErrors,
        ...errors,
      };

      // Validate arguments
      const hasErrors = Object.values(combinedErrors).some((error) => error);
      if (hasErrors) {
        throw new Error(
          "Please fix argument validation errors before executing",
        );
      }

      // Set loading state
      setPromptExecuting(promptName, true);
      setPromptError(promptName, null);
      setPromptResponse(promptName, null);

      // Create execution request
      const requestBody = createPromptExecutionRequest(promptName, args);

      // Make API call
      const { payload } = await api.mcpRpc(projectId, requestBody);

      // Extract result
      const mcpResult = extractMcpResult(payload);

      // Update state
      setPromptResponse(promptName, mcpResult);

      // Add to execution history
      const historyEntry = createExecutionHistoryEntry({
        id: requestBody.id,
        startTime,
        arguments: args,
        result: mcpResult,
        status: "success",
      });
      addPromptExecution(promptName, {
        ...historyEntry,
        arguments: args || {}, // Ensure arguments is always defined
      });
    } catch (err) {
      const errorMessage =
        err instanceof FpApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "An unexpected error occurred";

      // Update error state
      setPromptError(promptName, errorMessage);

      // Add to execution history
      const historyEntry = createExecutionHistoryEntry({
        id: `prompts/get:${startTime}`,
        startTime,
        arguments: arguments_,
        error: errorMessage,
        status: "error",
      });
      addPromptExecution(promptName, {
        ...historyEntry,
        arguments: arguments_ || {}, // Ensure arguments is always defined
      });
    } finally {
      setPromptExecuting(promptName, false);
    }
  }, [
    promptName,
    projectId,
    arguments_,
    argumentErrors,
    parsedParams,
    setPromptExecuting,
    setPromptResponse,
    setPromptError,
    addPromptExecution,
  ]);

  const clearResults = useCallback(() => {
    if (!promptName) {
      return;
    }

    setPromptResponse(promptName, null);
    setPromptError(promptName, null);
  }, [promptName, setPromptResponse, setPromptError]);

  return {
    isLoading,
    result,
    error,
    executePrompt,
    clearResults,
    canExecute:
      !!promptName &&
      Object.keys(argumentErrors).every((key) => !argumentErrors[key]),
  };
}
